/**
 * diskScan — desktop-only "let Ellie look through my files" helper.
 *
 * The Electron main process owns all filesystem access (the renderer is fully
 * sandboxed). This module wraps the `electronBridge.files` IPC surface, triages
 * the returned file list down to document-like candidates, and uploads the
 * selected ones through the SAME path as a manual upload (`uploadDocument` →
 * presigned S3 PUT → `/documents/create`), so they flow into the existing OCR /
 * extraction / knowledge-base pipeline with zero server-side special-casing.
 *
 * Grants are per-plan: a folder picked from one plan's chat is bound to that
 * plan in the main process, and every read is scope-checked against that plan's
 * granted roots — so one plan can never read another plan's folder.
 *
 * Scans are also deduped per-plan: what a plan has already pulled off disk is
 * remembered in the main process, so re-scanning a connected folder offers only
 * what is genuinely new. See scanProject for the two nets that do it.
 */

import { getElectronBridge, isElectron, type FolderGrant, type PulledFile, type ScannedFile } from './platform';
import { uploadDocument } from '../store/actions/notes';

/**
 * File types the backend document processor can actually extract text from
 * (see backend document/process worker: pdfjs, DOCX/XLSX parsers, image OCR,
 * Whisper audio, plain text). Anything outside this set is noise we don't want
 * to spend upload bandwidth or KB credits on.
 */
const EXT_MIME: Record<string, string> = {
  pdf:  'application/pdf',
  doc:  'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls:  'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  csv:  'text/csv',
  txt:  'text/plain',
  rtf:  'application/rtf',
  json: 'application/json',
  jpg:  'image/jpeg',
  jpeg: 'image/jpeg',
  png:  'image/png',
  webp: 'image/webp',
  heic: 'image/heic',
  gif:  'image/gif',
  tiff: 'image/tiff',
  tif:  'image/tiff',
  mp3:  'audio/mpeg',
  wav:  'audio/wav',
  m4a:  'audio/mp4',
};

/** Files larger than this are skipped — a misfiled video / disk image is never
 *  a document, and base64-inlining a huge file would blow renderer memory. */
export const MAX_SCAN_FILE_BYTES = 25 * 1024 * 1024; // 25 MB

/** True when running on desktop with the folder-access bridge available. */
export function isDiskScanAvailable(): boolean {
  return isElectron && !!getElectronBridge()?.files;
}

/** MIME type for a (dot-less, lowercased) extension, or octet-stream. */
export function mimeForExt(ext: string): string {
  return EXT_MIME[ext.toLowerCase()] ?? 'application/octet-stream';
}

/** True when a scanned file is a document-like candidate worth uploading. */
export function isCandidate(file: ScannedFile): boolean {
  return (
    !!EXT_MIME[file.ext] &&
    file.size > 0 &&
    file.size <= MAX_SCAN_FILE_BYTES
  );
}

/** Open the native folder picker and bind the chosen folder(s) to a plan. */
export async function grantFolder(projectId: string | number): Promise<FolderGrant[]> {
  const bridge = getElectronBridge();
  if (!bridge?.files) return [];
  return bridge.files.grant(projectId);
}

/** Folders currently connected to a plan. */
export async function listGrants(projectId: string | number): Promise<FolderGrant[]> {
  const bridge = getElectronBridge();
  if (!bridge?.files) return [];
  return bridge.files.grants(projectId);
}

/** Revoke one folder grant (or all of a plan's grants when path omitted). */
export async function revokeGrant(
  projectId: string | number,
  folderPath?: string,
): Promise<FolderGrant[]> {
  const bridge = getElectronBridge();
  if (!bridge?.files) return [];
  return bridge.files.revoke(projectId, folderPath);
}

/** Files this plan has already pulled off disk (the main-process ledger). */
export async function listPulled(projectId: string | number): Promise<PulledFile[]> {
  const bridge = getElectronBridge();
  if (!bridge?.files?.pulled) return [];
  return bridge.files.pulled(projectId).catch(() => []);
}

/**
 * Identity of one file in the "already pulled" ledger. Both sides of the IPC
 * build this from the same fields, and both round mtime — fs hands it over as a
 * float, so an unrounded key would never match a rounded one.
 *
 * The paths compare as raw strings on purpose: `scan` and the ledger both carry
 * paths produced by `path.join`/`path.normalize` in the main process, so they
 * are already normalized for the platform and byte-identical for the same file.
 */
export function scanLedgerKey(file: { path: string; size: number; mtime: number }): string {
  return `${file.path}|${file.size}|${Math.round(file.mtime)}`;
}

/** A document already on the plan, as returned by /documents/get-all. */
export interface PlanDocument {
  filename?: string | null;
  name?: string | null;
  [key: string]: any;
}

/**
 * Match key for reconciling a scan against the plan's server-side documents.
 *
 * Filename only, trimmed + lowercased: the backend keys a document on
 * (account_id, filename, project_id), so re-uploading a same-named file into
 * the same plan replaces and re-queues the existing record rather than adding a
 * second one. Matching on the same field mirrors what the server would do, and
 * catches the case the local ledger cannot — a file added by hand, or from
 * another machine, so it was never "pulled" on this device.
 */
export function planDocumentKey(name: string | null | undefined): string {
  return String(name ?? '').trim().toLowerCase();
}

export interface ScanResult {
  /** Every file found across the plan's granted folders. */
  all: ScannedFile[];
  /** The subset Ellie would actually upload (document-like, within size cap). */
  candidates: ScannedFile[];
  /** Document-like files held back because the plan already has them. */
  alreadyAdded: ScannedFile[];
}

export interface ScanOptions {
  /**
   * The plan's current documents (from `state.documents.list[projectId]`).
   * Used as a second dedup net alongside the local ledger, for files that were
   * uploaded manually or from another machine. Omit to rely on the ledger only.
   */
  planDocuments?: PlanDocument[] | null;
}

/**
 * Walk a plan's granted folders and split the results into all / candidates /
 * alreadyAdded.
 *
 * Scanning is stateless in the main process, so without this subtraction a
 * second scan of a connected folder re-offers every file the user already
 * added — and re-selecting them burns a second upload plus a second pass of
 * backend OCR / extraction / KB indexing. Two independent nets:
 *   • the local ledger  — exact file identity (path + size + mtime), so an
 *                         edited file is deliberately offered again;
 *   • the plan's documents — filename match, covering files this device never
 *                         uploaded itself.
 */
export async function scanProject(
  projectId: string | number,
  options: ScanOptions = {},
): Promise<ScanResult> {
  const bridge = getElectronBridge();
  if (!bridge?.files) return { all: [], candidates: [], alreadyAdded: [] };
  const [all, pulled] = await Promise.all([
    bridge.files.scan(projectId),
    listPulled(projectId),
  ]);

  const pulledKeys = new Set(pulled.map(scanLedgerKey));
  const documentNames = new Set(
    (options.planDocuments ?? [])
      .map((doc) => planDocumentKey(doc?.filename ?? doc?.name))
      .filter(Boolean),
  );

  const candidates: ScannedFile[] = [];
  const alreadyAdded: ScannedFile[] = [];
  for (const file of all) {
    if (!isCandidate(file)) continue;
    if (pulledKeys.has(scanLedgerKey(file)) || documentNames.has(planDocumentKey(file.name))) {
      alreadyAdded.push(file);
    } else {
      candidates.push(file);
    }
  }
  return { all, candidates, alreadyAdded };
}

export interface UploadProgress {
  /** Files finished (success or failure). */
  done: number;
  /** Total files in this batch. */
  total: number;
  /** Name of the file currently uploading. */
  current: string;
}

export interface UploadSummary {
  uploaded: ScannedFile[];
  failed: { file: ScannedFile; message: string }[];
}

/** Per-file outcome, emitted as each upload completes so a caller can correlate
 *  the new document id to its later Ably processing-completion event. */
export interface UploadFileResult {
  file: ScannedFile;
  success: boolean;
  /** New document id from /documents/create (present when success). */
  documentId?: string | number | null;
  /** 'processing' (will emit an Ably completion later) or 'active' (done now). */
  status?: string | null;
  message?: string;
}

/**
 * Mark one file as pulled into a plan so a later scan skips it. Never throws —
 * the ledger is an optimisation, and losing an entry only costs a re-offer.
 */
async function recordPulled(
  projectId: string | number,
  file: ScannedFile,
  documentId: string | number | null,
): Promise<void> {
  const bridge = getElectronBridge();
  if (!bridge?.files?.recordPull) return;
  try {
    await bridge.files.recordPull(projectId, [{
      path: file.path,
      name: file.name,
      size: file.size,
      mtime: file.mtime,
      documentId,
    }]);
  } catch { /* ignore — worst case the file is offered again next scan */ }
}

/**
 * Read each selected file's bytes (scope-checked in main) and push it through
 * the standard upload path. Sequential by design — keeps renderer memory flat
 * and avoids hammering the presigned-URL endpoint. `dispatch` is the Redux
 * dispatch from the calling screen. `onFileResult` fires once per file the
 * moment its upload resolves (before the next begins), carrying the new
 * document id + status so the caller can track backend processing via Ably.
 */
export async function uploadScannedFiles(
  projectId: string | number,
  files: ScannedFile[],
  dispatch: any,
  onProgress?: (p: UploadProgress) => void,
  onFileResult?: (r: UploadFileResult) => void,
  onFileStart?: (file: ScannedFile) => void,
): Promise<UploadSummary> {
  const bridge = getElectronBridge();
  const summary: UploadSummary = { uploaded: [], failed: [] };
  if (!bridge?.files) {
    files.forEach((file) => {
      summary.failed.push({ file, message: 'Desktop file access unavailable' });
      onFileResult?.({ file, success: false, message: 'Desktop file access unavailable' });
    });
    return summary;
  }

  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    onFileStart?.(file);
    onProgress?.({ done: i, total: files.length, current: file.name });
    try {
      const base64 = await bridge.files.read(projectId, file.path);
      const mime = mimeForExt(file.ext);
      // data: URI is handled by uploadDocument's web/Electron fetch path
      // (shouldUploadViaFetch → fetch(dataUri).blob() → PUT to S3).
      const dataUri = `data:${mime};base64,${base64}`;
      const result = await dispatch(
        uploadDocument(
          projectId,
          { filename: file.name, name: file.name, size: file.size, type: mime, mime },
          dataUri,
          mime,
        ),
      );
      if (result?.success) {
        summary.uploaded.push(file);
        // Remember it BEFORE reporting success, and one file at a time rather
        // than once per batch: a quit or crash midway through a long ingest
        // must not leave finished files unrecorded, or the next scan re-offers
        // them. Best-effort — a ledger write that fails is a re-offer, not a
        // failed upload, so it must never surface as one.
        await recordPulled(projectId, file, result?.payload?.id ?? null);
        onFileResult?.({
          file,
          success: true,
          documentId: result?.payload?.id ?? null,
          status: result?.payload?.status ?? null,
        });
      } else {
        summary.failed.push({ file, message: result?.message || 'Upload failed' });
        onFileResult?.({ file, success: false, message: result?.message || 'Upload failed' });
      }
    } catch (err: any) {
      summary.failed.push({ file, message: err?.message || 'Could not read file' });
      onFileResult?.({ file, success: false, message: err?.message || 'Could not read file' });
    }
  }

  onProgress?.({ done: files.length, total: files.length, current: '' });
  return summary;
}

/** Human-readable byte size (e.g. "1.4 MB"). */
// Canonical byte formatter lives in formatFileSize (#2081 audit consolidation);
// re-exported because this module's consumers (folder-scan sheets) import it
// from here alongside the scan API.
export { formatBytes } from './formatFileSize';
